// frontend/src/components/StockTransferList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Search, Filter, Download, Eye, Edit, Trash2,
  Plus, Package, Warehouse, DollarSign, Calendar,
  FileText, Hash, RefreshCw, Building,
  Truck, CheckCircle, AlertCircle, XCircle,
  Loader2, X, ChevronDown, ChevronUp, Globe, ArrowLeftRight,
  MapPin, Clock, Check, TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle,
  Link2, List
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./StockTransferList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const StockTransferList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState({
    status: "",
    from_warehouse_id: "",
    to_warehouse_id: "",
    product_id: "",
    start_date: "",
    end_date: "",
  });

  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [stats, setStats] = useState({
    totalTransfers: 0,
    totalQuantity: 0,
    totalValue: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';

  useEffect(() => {
    fetchData();
    fetchDropdowns();

    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams();
      if (filter.status) queryParams.append("status", filter.status);
      if (filter.from_warehouse_id) queryParams.append("from_warehouse_id", filter.from_warehouse_id);
      if (filter.to_warehouse_id) queryParams.append("to_warehouse_id", filter.to_warehouse_id);
      if (filter.product_id) queryParams.append("product_id", filter.product_id);
      if (filter.start_date) queryParams.append("start_date", filter.start_date);
      if (filter.end_date) queryParams.append("end_date", filter.end_date);
      if (isSystemAdmin && selectedCompany) queryParams.append("company_id", selectedCompany);
      if (searchTerm) queryParams.append("search", searchTerm);

      const queryString = queryParams.toString();
      const apiUrl = `${url}/api/stocktransfers${queryString ? `?${queryString}` : ""}`;

      const response = await axios.get(apiUrl);

      if (response.data.success) {
        setTransfers(response.data.data);
        setStats(response.data.stats || {
          totalTransfers: 0,
          totalQuantity: 0,
          totalValue: 0,
          pending: 0,
          completed: 0,
          cancelled: 0,
        });
      }
    } catch (error) {
      toast.error("Failed to load stock transfers");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const params = {};
      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }

      const [warehousesRes, productsRes] = await Promise.all([
        axios.get(`${url}/api/warehouse/dropdown`, { params }),
        axios.get(`${url}/api/products/dropdown`, { params })
      ]);

      if (warehousesRes.data.success) setWarehouses(warehousesRes.data.data || []);
      if (productsRes.data.success) setProducts(productsRes.data.data || []);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchData();
  };

  const resetFilters = () => {
    setFilter({
      status: "",
      from_warehouse_id: "",
      to_warehouse_id: "",
      product_id: "",
      start_date: "",
      end_date: "",
    });
    setSelectedCompany("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchData();
  };

  const handleDelete = async (id, referenceNo) => {
    if (!window.confirm(`Are you sure you want to cancel transfer "${referenceNo}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/stocktransfers/${id}`);

      if (response.data.success) {
        toast.success(`Transfer "${referenceNo}" cancelled successfully`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to cancel transfer");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getProductInfo = (transfer) => {
    if (transfer.product_id) {
      if (typeof transfer.product_id === 'object') {
        return {
          name: transfer.product_id.product_name || "Unknown Product",
          code: transfer.product_id.product_code || "N/A"
        };
      }
    }
    return { name: "Unknown Product", code: "N/A" };
  };

  const getWarehouseInfo = (warehouse) => {
    if (warehouse) {
      if (typeof warehouse === 'object') {
        return {
          name: warehouse.warehouse_name || "Unknown Warehouse",
          code: warehouse.warehouse_code || "N/A"
        };
      }
    }
    return { name: "Unknown Warehouse", code: "N/A" };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fff3cd', text: '#856404', label: 'Pending' },
      completed: { bg: '#d4edda', text: '#155724', label: 'Completed' },
      cancelled: { bg: '#f8d7da', text: '#721c24', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || { bg: '#e2e3e5', text: '#383d41', label: status };
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.text
      }}>
        {config.label}
      </span>
    );
  };

  // Get transfer type based on direction
  const getTransferTypeInfo = (transfer) => {
    const fromName = transfer.from_warehouse_id?.warehouse_name || '';
    const toName = transfer.to_warehouse_id?.warehouse_name || '';
    
    if (fromName.includes('Main')) {
      return {
        type: 'Out',
        icon: <ArrowUpCircle size={12} />,
        bg: '#fff2f0',
        color: '#ff4d4f'
      };
    } else if (toName.includes('Main')) {
      return {
        type: 'In',
        icon: <ArrowDownCircle size={12} />,
        bg: '#f6ffed',
        color: '#52c41a'
      };
    } else {
      return {
        type: 'Transfer',
        icon: <ArrowLeftRight size={12} />,
        bg: '#e6f7ff',
        color: '#1890ff'
      };
    }
  };

  // Filter transfers based on search term
  const filteredTransfers = transfers.filter(transfer => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const productInfo = getProductInfo(transfer);
    const fromWarehouse = getWarehouseInfo(transfer.from_warehouse_id);
    const toWarehouse = getWarehouseInfo(transfer.to_warehouse_id);
    
    return (
      (transfer.reference_no && transfer.reference_no.toLowerCase().includes(searchLower)) ||
      (transfer.transfer_id && transfer.transfer_id.toString().includes(searchLower)) ||
      productInfo.name.toLowerCase().includes(searchLower) ||
      productInfo.code.toLowerCase().includes(searchLower) ||
      fromWarehouse.name.toLowerCase().includes(searchLower) ||
      fromWarehouse.code.toLowerCase().includes(searchLower) ||
      toWarehouse.name.toLowerCase().includes(searchLower) ||
      toWarehouse.code.toLowerCase().includes(searchLower) ||
      (transfer.description && transfer.description.toLowerCase().includes(searchLower)) ||
      (transfer.batch_number && transfer.batch_number.toLowerCase().includes(searchLower))
    );
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransfers = filteredTransfers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTransfers.length / itemsPerPage);

  const goToCreate = () => {
    navigate("/AddStockTransfer");
  };

  const goToEdit = (id) => {
    navigate(`/stocktransfers/edit/${id}`);
  };

  const goToView = (id) => {
    navigate(`/stocktransfers/view/${id}`);
  };

  const handleExport = () => {
    if (filteredTransfers.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const headers = [
        "Reference No", "Transfer ID", "Product", "Product Code",
        "From Warehouse", "From Code", "To Warehouse", "To Code",
        "Quantity", "Unit Cost", "Total Value", "Transfer Date",
        "Status", "Batch No", "Description"
      ];
      
      const csvData = filteredTransfers.map(transfer => {
        const productInfo = getProductInfo(transfer);
        const fromWarehouse = getWarehouseInfo(transfer.from_warehouse_id);
        const toWarehouse = getWarehouseInfo(transfer.to_warehouse_id);
        
        return [
          transfer.reference_no || "",
          transfer.transfer_id || "",
          productInfo.name,
          productInfo.code,
          fromWarehouse.name,
          fromWarehouse.code,
          toWarehouse.name,
          toWarehouse.code,
          transfer.quantity || 0,
          transfer.unit_cost || 0,
          transfer.total_cost || 0,
          formatDate(transfer.transfer_date),
          transfer.status || "",
          transfer.batch_number || "",
          transfer.description || ""
        ];
      });

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => 
          typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
        ).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock-transfers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading stock transfers...</p>
      </div>
    );
  }

  return (
    <div className="stock-transfer-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>
            <Truck size={24} style={{ display: 'inline', marginRight: '10px' }} />
            Stock Transfers
          </h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
                {selectedCompany && companies.find(c => c._id === selectedCompany) && (
                  <> - {companies.find(c => c._id === selectedCompany)?.company_name}</>
                )}
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>
        
        <div className="header-actions">
          <button 
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          
          <button onClick={goToCreate} className="add-button">
            <Plus size={16} /> New Transfer
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e6f7ff', color: '#1890ff' }}>
            <Truck size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.totalTransfers || 0}</div>
            <div className="stat-label">Total Transfers</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f6ffed', color: '#52c41a' }}>
            <Package size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{(stats.totalQuantity || 0).toLocaleString()}</div>
            <div className="stat-label">Total Quantity</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f0f5ff', color: '#2f54eb' }}>
            <DollarSign size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{formatCurrency(stats.totalValue || 0)}</div>
            <div className="stat-label">Total Value</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fff3cd', color: '#856404' }}>
            <Clock size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.pending || 0}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#d4edda', color: '#155724' }}>
            <Check size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.completed || 0}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-container">
          <div className="filter-header">
            <Filter size={18} />
            <span>Filter Transfers</span>
            <button onClick={resetFilters} className="clear-filters">
              Clear All
            </button>
          </div>
          
          <div className="filter-grid">
            {/* Company Filter (System Admin Only) */}
            {isSystemAdmin && (
              <div className="filter-group">
                <label>Company</label>
                <div className="select-wrapper">
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">All Companies</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
              </div>
            )}
            
            <div className="filter-group">
              <label>Status</label>
              <div className="select-wrapper">
                <select 
                  name="status" 
                  value={filter.status} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <ChevronDown size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>From Warehouse</label>
              <div className="select-wrapper">
                <select 
                  name="from_warehouse_id" 
                  value={filter.from_warehouse_id} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Warehouses</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse._id} value={warehouse._id}>
                      {warehouse.warehouse_code} - {warehouse.warehouse_name}
                    </option>
                  ))}
                </select>
                <Warehouse size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>To Warehouse</label>
              <div className="select-wrapper">
                <select 
                  name="to_warehouse_id" 
                  value={filter.to_warehouse_id} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Warehouses</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse._id} value={warehouse._id}>
                      {warehouse.warehouse_code} - {warehouse.warehouse_name}
                    </option>
                  ))}
                </select>
                <Warehouse size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Product</label>
              <div className="select-wrapper">
                <select 
                  name="product_id" 
                  value={filter.product_id} 
                  onChange={handleFilterChange}
                >
                  <option value="">All Products</option>
                  {products.map(product => (
                    <option key={product._id} value={product._id}>
                      {product.product_name} ({product.product_code})
                    </option>
                  ))}
                </select>
                <Package size={16} className="select-icon" />
              </div>
            </div>

            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filter.start_date}
                onChange={handleFilterChange}
                className="date-input"
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={filter.end_date}
                onChange={handleFilterChange}
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button onClick={applyFilters} className="apply-filters">
              <Filter size={16} /> Apply Filters
            </button>
            <button onClick={fetchData} className="refresh-btn">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={handleExport} className="export-btn">
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by reference, product, warehouse, description..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              <X size={14} />
            </button>
          )}
        </div>
        
        <div className="search-actions">
          <div className="stats">
            <span>Showing {filteredTransfers.length} of {transfers.length} transfers</span>
          </div>
        </div>
      </div>

      {/* Transfers Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Product</th>
              <th>From Warehouse</th>
              <th>To Warehouse</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Unit Cost</th>
              <th>Total</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTransfers.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Truck size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {transfers.length === 0
                        ? "No stock transfers found. Create your first transfer!"
                        : "No transfers match your search criteria."
                      }
                    </p>
                    {transfers.length === 0 && (
                      <button 
                        className="add-button" 
                        onClick={goToCreate}
                      >
                        <Plus size={16} /> Create First Transfer
                      </button>
                    )}
                    {transfers.length > 0 && (
                      <button 
                        className="clear-filters-btn"
                        onClick={resetFilters}
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              currentTransfers.map((transfer) => {
                const productInfo = getProductInfo(transfer);
                const fromWarehouse = getWarehouseInfo(transfer.from_warehouse_id);
                const toWarehouse = getWarehouseInfo(transfer.to_warehouse_id);
                const isEditable = transfer.status !== 'cancelled';
                
                const typeInfo = getTransferTypeInfo(transfer);
                
                return (
                  <tr key={transfer._id}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600 }}>
                          {transfer.reference_no || `TRF${transfer.transfer_id}`}
                        </span>
                        <span style={{ fontSize: '10px', color: '#999' }}>
                          #{transfer.transfer_id}
                        </span>
                      </div>
                    </td>
                    
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Package size={14} color="#666" />
                        <div>
                          <div style={{ fontWeight: 500 }}>{productInfo.name}</div>
                          <div style={{ fontSize: '10px', color: '#999' }}>{productInfo.code}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MapPin size={14} color="#ff4d4f" />
                        <div>
                          <div>{fromWarehouse.name}</div>
                          <div style={{ fontSize: '10px', color: '#999' }}>{fromWarehouse.code}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MapPin size={14} color="#52c41a" />
                        <div>
                          <div>{toWarehouse.name}</div>
                          <div style={{ fontSize: '10px', color: '#999' }}>{toWarehouse.code}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: typeInfo.bg,
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: typeInfo.color
                      }}>
                        {typeInfo.icon}
                        {typeInfo.type}
                      </span>
                    </td>
                    
                    <td>
                      <span style={{ 
                        fontWeight: 600,
                        color: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <TrendingUp size={12} />
                        {transfer.quantity.toLocaleString()}
                      </span>
                    </td>
                    
                    <td>{formatCurrency(transfer.unit_cost)}</td>
                    
                    <td>
                      <span style={{ fontWeight: 600, color: '#722ed1' }}>
                        {formatCurrency(transfer.total_cost)}
                      </span>
                    </td>
                    
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={12} color="#fa8c16" />
                        {formatDate(transfer.transfer_date)}
                      </div>
                    </td>
                    
                    <td>
                      {getStatusBadge(transfer.status)}
                    </td>
                    
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          className="action-btn view-btn"
                          onClick={() => goToView(transfer._id)}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        
                        <button
                          className="action-btn edit-btn"
                          onClick={() => goToEdit(transfer._id)}
                          title="Edit"
                          disabled={!isEditable}
                        >
                          <Edit size={14} />
                        </button>
                        
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(transfer._id, transfer.reference_no)}
                          title="Cancel Transfer"
                          disabled={!isEditable}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
            
            <div className="pagination-info">
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTransfers.length)} of {filteredTransfers.length} entries
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockTransferList;